import React, { useContext, useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  Avatar,
  Chip,
  Paper,
} from '@mui/material';
import { 
  Settings,
  AdminPanelSettings,
  Logout,
  Groups as GroupsIcon,
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Folder as FolderIcon,
  Book as BookIcon,
  ManageAccounts as ManageAccountsIcon,
  Science as ScienceIcon,
  Close as CloseIcon,
  Insights as InsightsIcon,
  PlaylistAddCheck as PlaylistAddCheckIcon
} from '@mui/icons-material';
import { AuthContext } from '../context/AuthContext';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { PERMISSIONS } from '../config/permissions';
import { USER_ROLES } from '../config/constants';
import { usePermissions } from '../hooks/usePermissions';

export default function NavBar() {
  const { user, role, logout, loading } = useContext(AuthContext);
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (loading) return null;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    try {
      setAnchorEl(null);
      setMobileOpen(false);
      const { error } = await logout();
      
      if (error) {
        throw error;
      }
      
      localStorage.removeItem('selectedProject');
      sessionStorage.clear(); 
      
      window.location.href = '/login'; 
    } catch (error) {
      console.error('Logout error:', error.message);
      alert('Er ging iets mis bij het uitloggen. Probeer het opnieuw.');
    }
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (path) => {
    handleClose();
    setMobileOpen(false);
    navigate(path);
  };

  const handleDrawerItemClick = (path) => {
    setMobileOpen(false);
    navigate(path);
  }

  const isActivePath = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const getRoleDisplayName = (userRole) => {
    switch (userRole) {
      case USER_ROLES.ADMIN:
        return 'Beheerder';
      case USER_ROLES.PROJECT_MANAGER:
        return 'Projectmanager';
      case USER_ROLES.EMPLOYEE:
        return 'Medewerker';
      default:
        return 'Gebruiker';
    }
  };

  const getRoleColor = (userRole) => {
    switch (userRole) {
      case USER_ROLES.ADMIN:
        return 'error';
      case USER_ROLES.PROJECT_MANAGER:
        return 'warning';
      case USER_ROLES.EMPLOYEE:
        return 'info';
      default:
        return 'default';
    }
  };

  const drawer = (
    <Box sx={{ 
      height: '100%',
      bgcolor: 'background.paper',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Drawer Header */}
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'primary.main',
        color: 'primary.contrastText'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <img 
            src="/logo192.png" 
            alt="BEM.report Logo" 
            style={{ 
              width: 24, 
              height: 24,
              borderRadius: '4px',
              filter: 'brightness(0) invert(1)'
            }} 
          />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            BEM.report
          </Typography>
        </Box>
        <IconButton 
          onClick={handleDrawerToggle}
          sx={{ color: 'primary.contrastText' }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* User Info Section */}
      <Paper elevation={0} sx={{ 
        m: 2, 
        p: 2, 
        bgcolor: 'grey.50',
        borderRadius: 2,
        border: 1,
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ 
            bgcolor: 'primary.main',
            width: 40,
            height: 40
          }}>
            {user?.email?.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ 
              fontWeight: 600,
              color: 'text.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {user?.email}
      </Typography>
            <Chip 
              label={getRoleDisplayName(role)}
              size="small"
              color={getRoleColor(role)}
              sx={{ mt: 0.5, fontSize: '0.75rem' }}
            />
          </Box>
        </Box>
      </Paper>

      {/* Navigation Items */}
      <List sx={{ flexGrow: 1, px: 1 }}>
        <ListItem disablePadding>
          <ListItemButton 
            onClick={() => handleDrawerItemClick('/')}
            selected={isActivePath('/')}
            sx={{ 
              borderRadius: 1,
              mx: 1,
              mb: 0.5,
              '&.Mui-selected': {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
                '& .MuiListItemIcon-root': {
                  color: 'primary.contrastText'
                }
              }
            }}
          >
            <ListItemIcon>
              <DashboardIcon />
            </ListItemIcon>
            <ListItemText primary="Dashboard" />
          </ListItemButton>
        </ListItem>

        {hasPermission(PERMISSIONS.VIEW_PROJECT) && (
          <ListItem disablePadding>
            <ListItemButton 
              onClick={() => handleDrawerItemClick('/projects')}
              selected={isActivePath('/projects')}
              sx={{ 
                borderRadius: 1,
                mx: 1,
                mb: 0.5,
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'primary.contrastText'
                  }
                }
              }}
            >
              <ListItemIcon>
                <FolderIcon />
              </ListItemIcon>
              <ListItemText primary="Projecten" />
            </ListItemButton>
          </ListItem>
        )}

        {(role === USER_ROLES.ADMIN || role === USER_ROLES.PROJECT_MANAGER) && (
          <ListItem disablePadding>
            <ListItemButton 
              onClick={() => handleDrawerItemClick('/library')}
              selected={isActivePath('/library')}
              sx={{ 
                borderRadius: 1,
                mx: 1,
                mb: 0.5,
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'primary.contrastText'
                  }
                }
              }}
            >
              <ListItemIcon>
                <BookIcon />
              </ListItemIcon>
              <ListItemText primary="Bibliotheek" />
            </ListItemButton>
          </ListItem>
        )}

        {(role === USER_ROLES.ADMIN || role === USER_ROLES.PROJECT_MANAGER) && (
          <ListItem disablePadding>
            <ListItemButton 
              onClick={() => handleDrawerItemClick('/project-management')}
              selected={isActivePath('/project-management')}
              sx={{ 
                borderRadius: 1,
                mx: 1,
                mb: 0.5,
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'primary.contrastText'
                  }
                }
              }}
            >
              <ListItemIcon>
                <ManageAccountsIcon />
              </ListItemIcon>
              <ListItemText primary="Projectbeheer" />
            </ListItemButton>
          </ListItem>
        )}

        {hasPermission(PERMISSIONS.VIEW_METRICS) && (
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => handleDrawerItemClick('/metrics')}
              selected={isActivePath('/metrics')}
              sx={{
                borderRadius: 1,
                mx: 1,
                mb: 0.5,
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'primary.contrastText'
                  }
                }
              }}
            >
              <ListItemIcon>
                <InsightsIcon />
              </ListItemIcon>
              <ListItemText primary="Metrics" />
            </ListItemButton>
          </ListItem>
        )}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar 
        position="fixed"
        elevation={0}
        sx={{ 
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          backdropFilter: 'blur(10px)',
          borderBottom: 1,
          borderColor: 'rgba(255, 255, 255, 0.1)',
          zIndex: (theme) => theme.zIndex.drawer + 1
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
          {isMobile ? (
            <>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ 
                  mr: 2,
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.2)'
                  }
                }}
              >
                <MenuIcon />
              </IconButton>
              <Link
                to="/"
                style={{ 
                  color: 'inherit', 
                  display: 'flex', 
                  alignItems: 'center',
                  textDecoration: 'none',
                  marginRight: '8px'
                }}
              >
                <img 
                  src="/logo192.png" 
                  alt="BEM.report Logo" 
                  style={{ 
                    width: 28, 
                    height: 28,
                    borderRadius: '4px',
                    filter: 'brightness(0) invert(1)'
                  }} 
                />
              </Link>
              <Typography
                variant="h6"
                component={Link}
                to="/"
                sx={{
                  color: "inherit",
                  textDecoration: 'none',
                  display: { xs: 'none', sm: 'block' },
                  fontWeight: 600,
                  letterSpacing: 0.5
                }}>
                BEM.report
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <IconButton
                size="large"
                onClick={handleMenu}
                color="inherit"
                edge="end"
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.2)'
                  }
                }}
              >
                <Avatar sx={{ 
                  width: 32, 
                  height: 32, 
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  fontSize: '0.875rem'
                }}>
                  {user?.email?.charAt(0).toUpperCase()}
                </Avatar>
              </IconButton>
            </>
          ) : (
            <>
              <Link 
                to="/" 
                style={{ 
                  color: 'inherit', 
                  display: 'flex', 
                  alignItems: 'center',
                  textDecoration: 'none',
                  marginRight: '16px'
                }}
              >
                <img 
                  src="/logo192.png" 
                  alt="BEM.report Logo" 
                  style={{ 
                    width: 32, 
                    height: 32, 
                    marginRight: 12,
                    borderRadius: '6px',
                    filter: 'brightness(0) invert(1)'
                  }} 
                />
                <Typography variant="h6" sx={{ fontWeight: 600, letterSpacing: 0.5 }}>
                  BEM.report
              </Typography>
              </Link>
              
              <Box sx={{ flexGrow: 1 }} />
              
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Button 
                  color="inherit" 
                  component={Link} 
                  to="/"
                  sx={{ 
                    borderRadius: 1,
                    px: 1.5,
                    py: 0.75,
                    fontWeight: 500,
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.05)'
                    }
                  }}
                >
                Dashboard
              </Button>
              {hasPermission(PERMISSIONS.VIEW_PROJECT) && (
                  <Button 
                    color="inherit" 
                    component={Link} 
                    to="/projects"
                    sx={{ 
                      borderRadius: 1,
                      px: 1.5,
                      py: 0.75,
                      fontWeight: 500,
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.05)'
                      }
                    }}
                  >
                  Projecten
                </Button>
              )}
              {(role === USER_ROLES.ADMIN || role === USER_ROLES.PROJECT_MANAGER) && (
                  <Button 
                    color="inherit" 
                    component={Link} 
                    to="/library"
                    sx={{ 
                      borderRadius: 1,
                      px: 1.5,
                      py: 0.75,
                      fontWeight: 500,
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.05)'
                      }
                    }}
                  >
                  Bibliotheek
                </Button>
              )}
              {(role === USER_ROLES.ADMIN || role === USER_ROLES.PROJECT_MANAGER) && (
                  <Button 
                    color="inherit" 
                    component={Link} 
                    to="/project-management"
                    sx={{ 
                      borderRadius: 1,
                      px: 1.5,
                      py: 0.75,
                      fontWeight: 500,
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.05)'
                      }
                    }}
                  >
                  Projectbeheer
                </Button>
              )}
              {hasPermission(PERMISSIONS.VIEW_METRICS) && (
                  <Button
                    color="inherit"
                    component={Link}
                    to="/metrics"
                    sx={{
                      borderRadius: 1,
                      px: 1.5,
                      py: 0.75,
                      fontWeight: 500,
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.05)'
                      }
                    }}
                  >
                  Metrics
                </Button>
              )}
              </Box>
              
              <Box sx={{ ml: 2 }}>
              <IconButton
                size="large"
                onClick={handleMenu}
                color="inherit"
                edge="end"
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.2)'
                    }
                  }}
              >
                  <Avatar sx={{ 
                    width: 36, 
                    height: 36, 
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    fontSize: '1rem',
                    fontWeight: 600
                  }}>
                    {user?.email?.charAt(0).toUpperCase()}
                  </Avatar>
              </IconButton>
              </Box>
            </>
          )}
          
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            sx={{ 
              mt: 1,
              '& .MuiPaper-root': {
                borderRadius: 2,
                minWidth: 200,
                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                border: 1,
                borderColor: 'divider'
              }
            }}
          >
            {/* User Info Header */}
            <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {user?.email}
              </Typography>
              <Chip 
                label={getRoleDisplayName(role)}
                size="small"
                color={getRoleColor(role)}
                sx={{ mt: 0.5, fontSize: '0.75rem' }}
              />
            </Box>
            
            <MenuItem onClick={() => handleMenuItemClick('/settings')} sx={{ py: 1.5 }}>
              <ListItemIcon>
                <Settings fontSize="small" />
              </ListItemIcon>
              <ListItemText>Instellingen</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleMenuItemClick('/stof-suggesties')} sx={{ py: 1.5 }}>
              <ListItemIcon>
                <ScienceIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Stofsuggesties</ListItemText>
            </MenuItem>
            {hasPermission(PERMISSIONS.MANAGE_STOFFENPAKKETTEN) && (
              <MenuItem onClick={() => handleMenuItemClick('/stoffenpakketten')} sx={{ py: 1.5 }}>
                <ListItemIcon>
                  <PlaylistAddCheckIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Stoffenpakketten</ListItemText>
              </MenuItem>
            )}
            {hasPermission(PERMISSIONS.MANAGE_EMPLOYEES) && (
              <MenuItem onClick={() => handleMenuItemClick('/employee-management')} sx={{ py: 1.5 }}>
                <ListItemIcon>
                  <GroupsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Werknemer Beheer</ListItemText>
              </MenuItem>
            )}
            {hasPermission(PERMISSIONS.MANAGE_USERS) && (
              <MenuItem onClick={() => handleMenuItemClick('/admin')} sx={{ py: 1.5 }}>
                <ListItemIcon>
                  <AdminPanelSettings fontSize="small" />
                </ListItemIcon>
                <ListItemText>Admin Panel</ListItemText>
              </MenuItem>
            )}
            <Divider />
            <MenuItem onClick={handleLogout} sx={{ py: 1.5, color: 'error.main' }}>
              <ListItemIcon>
                <Logout fontSize="small" sx={{ color: 'error.main' }} />
              </ListItemIcon>
              <ListItemText>Uitloggen</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="nav">
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: 280,
              borderRadius: 0
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
    </>
  );
}
